import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import MediaPicker from "@/components/MediaPicker";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, ArrowUp, ArrowDown, ChevronRight } from "lucide-react";

function emptyItem(category_id) {
  return {
    category_id: category_id || "",
    name: { hr: "", en: "" },
    description: { hr: "", en: "" },
    price: 0,
    currency: "EUR",
    image_url: null,
    allergens: [],
    is_available: true,
  };
}

function ItemDialog({ open, onOpenChange, value, categories, onSave }) {
  const [it, setIt] = useState(value);
  useEffect(() => setIt(value), [value]);
  if (!it) return null;
  const set = (path) => (v) => {
    setIt((cur) => {
      const next = { ...cur };
      const segs = path.split(".");
      let c = next;
      for (let i = 0; i < segs.length - 1; i++) {
        c[segs[i]] = { ...(c[segs[i]] || {}) };
        c = c[segs[i]];
      }
      c[segs[segs.length - 1]] = v;
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{value?.id ? "Edit item" : "New item"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={it.category_id} onValueChange={set("category_id")}>
              <SelectTrigger data-testid="item-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name?.hr || c.name?.en}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Name (HR)</Label>
              <Input value={it.name.hr} onChange={(e) => set("name.hr")(e.target.value)} data-testid="item-name-hr" />
            </div>
            <div className="space-y-2">
              <Label>Name (EN)</Label>
              <Input value={it.name.en} onChange={(e) => set("name.en")(e.target.value)} data-testid="item-name-en" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Description (HR)</Label>
              <Textarea rows={2} value={it.description.hr} onChange={(e) => set("description.hr")(e.target.value)} />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Description (EN)</Label>
              <Textarea rows={2} value={it.description.en} onChange={(e) => set("description.en")(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Price</Label>
              <Input
                type="number"
                step="0.01"
                value={it.price}
                onChange={(e) => set("price")(parseFloat(e.target.value || "0"))}
                data-testid="item-price"
              />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Input value={it.currency} onChange={(e) => set("currency")(e.target.value)} />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Allergens (comma separated)</Label>
              <Input
                value={(it.allergens || []).join(", ")}
                onChange={(e) => set("allergens")(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                data-testid="item-allergens"
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Image</Label>
              <MediaPicker
                value={typeof it.image_url === "string" ? { url: it.image_url } : it.image_url}
                onChange={(v) => set("image_url")(v ? (v.paths || v.url) : null)}
                testid="item-image-picker"
              />
            </div>
            <div className="flex items-center gap-2 col-span-2">
              <Checkbox
                id="ia"
                checked={it.is_available}
                onCheckedChange={(v) => set("is_available")(Boolean(v))}
                data-testid="item-available"
              />
              <Label htmlFor="ia">Available</Label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => onSave(it)}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="item-save"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({ open, onOpenChange, value, onSave }) {
  const [cat, setCat] = useState(value);
  useEffect(() => setCat(value), [value]);
  if (!cat) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{value?.id ? "Edit category" : "New category"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label>Slug</Label>
            <Input value={cat.slug || ""} onChange={(e) => setCat({ ...cat, slug: e.target.value })} data-testid="cat-slug" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Name (HR)</Label>
              <Input value={cat.name?.hr || ""} onChange={(e) => setCat({ ...cat, name: { ...cat.name, hr: e.target.value } })} data-testid="cat-name-hr" />
            </div>
            <div className="space-y-2">
              <Label>Name (EN)</Label>
              <Input value={cat.name?.en || ""} onChange={(e) => setCat({ ...cat, name: { ...cat.name, en: e.target.value } })} data-testid="cat-name-en" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="ca" checked={cat.is_active !== false} onCheckedChange={(v) => setCat({ ...cat, is_active: Boolean(v) })} />
            <Label htmlFor="ca">Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => onSave(cat)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="cat-save">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminMenu() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCat, setEditingCat] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: cats } = await api.get("/admin/menu/categories");
      setCategories(cats);
      if (cats.length && !activeCat) setActiveCat(cats[0].id);
      const targetCat = activeCat || cats[0]?.id;
      if (targetCat) {
        const { data: its } = await api.get("/admin/menu/items", { params: { category_id: targetCat } });
        setItems(its);
      } else {
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [activeCat]);

  useEffect(() => { load(); }, [load]);

  const saveItem = async (it) => {
    try {
      if (it.id) {
        const payload = { ...it };
        delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.sort_order;
        await api.patch(`/admin/menu/items/${it.id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/admin/menu/items", it);
        toast.success("Item created");
      }
      setEditingItem(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveCat = async (c) => {
    try {
      if (c.id) {
        const payload = { slug: c.slug, name: c.name, is_active: c.is_active };
        await api.patch(`/admin/menu/categories/${c.id}`, payload);
        toast.success("Category updated");
      } else {
        await api.post("/admin/menu/categories", c);
        toast.success("Category created");
      }
      setEditingCat(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteItem = async (id) => {
    try { await api.delete(`/admin/menu/items/${id}`); toast.success("Item deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteCat = async (id) => {
    try { await api.delete(`/admin/menu/categories/${id}`); toast.success("Category deleted"); setActiveCat(null); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const reorder = async (collection, ids) => {
    const path = collection === "cat" ? "/admin/menu/categories/reorder" : "/admin/menu/items/reorder";
    try { await api.post(path, { ids }); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const moveCat = async (idx, dir) => {
    const next = [...categories];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setCategories(next);
    await reorder("cat", next.map((c) => c.id));
  };

  const moveItem = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    await reorder("item", next.map((i) => i.id));
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-menu-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80">CMS</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Menu manager</h1>
          </div>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="grid lg:grid-cols-[280px_1fr] gap-6">
            <div className="border border-border rounded-xl bg-card p-3" data-testid="categories-panel">
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="text-xs uppercase tracking-widest text-primary">Categories</span>
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setEditingCat({ name: { hr: "", en: "" }, slug: "", is_active: true })} data-testid="cat-new-button">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              <ul className="mt-2 space-y-1">
                {categories.map((c, idx) => (
                  <li
                    key={c.id}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md cursor-pointer ${activeCat === c.id ? "bg-primary/10 text-primary" : "hover:bg-secondary"}`}
                    onClick={() => setActiveCat(c.id)}
                    data-testid={`cat-row-${c.id}`}
                  >
                    <ChevronRight className={`w-3 h-3 transition ${activeCat === c.id ? "rotate-90 text-primary" : "text-muted-foreground"}`} />
                    <span className="flex-1 text-sm truncate">{c.name?.hr || c.name?.en}</span>
                    {!c.is_active && <span className="text-[10px] text-muted-foreground">off</span>}
                    <button type="button" className="p-1 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); moveCat(idx, -1); }} data-testid={`cat-up-${c.id}`}><ArrowUp className="w-3 h-3" /></button>
                    <button type="button" className="p-1 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); moveCat(idx, 1); }} data-testid={`cat-down-${c.id}`}><ArrowDown className="w-3 h-3" /></button>
                    <button type="button" className="p-1 text-muted-foreground hover:text-primary" onClick={(e) => { e.stopPropagation(); setEditingCat(c); }} data-testid={`cat-edit-${c.id}`}><Pencil className="w-3 h-3" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button type="button" className="p-1 text-muted-foreground hover:text-destructive" onClick={(e) => e.stopPropagation()} data-testid={`cat-delete-${c.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete category?</AlertDialogTitle>
                          <AlertDialogDescription>This will also delete all items in this category.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteCat(c.id)} className="bg-destructive text-destructive-foreground" data-testid={`cat-delete-confirm-${c.id}`}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="items-panel">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-widest text-primary">Items</span>
                <Button onClick={() => setEditingItem(emptyItem(activeCat))} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="item-new-button">
                  <Plus className="w-4 h-4 mr-1" /> New item
                </Button>
              </div>
              <div className="space-y-2">
                {items.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-lg">No items in this category.</div>}
                {items.map((it, idx) => (
                  <div key={it.id} className="flex items-center gap-3 border border-border rounded-lg p-3 bg-card" data-testid={`item-row-${it.id}`}>
                    <div className="flex flex-col">
                      <button type="button" className="p-1 text-muted-foreground hover:text-foreground" onClick={() => moveItem(idx, -1)} data-testid={`item-up-${it.id}`}><ArrowUp className="w-3 h-3" /></button>
                      <button type="button" className="p-1 text-muted-foreground hover:text-foreground" onClick={() => moveItem(idx, 1)} data-testid={`item-down-${it.id}`}><ArrowDown className="w-3 h-3" /></button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{it.name?.hr} / {it.name?.en}</div>
                      <div className="text-xs text-muted-foreground truncate">{it.description?.hr}</div>
                    </div>
                    <div className="text-primary text-sm">€{Number(it.price).toFixed(2)}</div>
                    <Button variant="outline" size="sm" onClick={() => setEditingItem(it)} className="border-border" data-testid={`item-edit-${it.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="border-border text-destructive" data-testid={`item-delete-${it.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete item?</AlertDialogTitle>
                          <AlertDialogDescription>{it.name?.hr}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteItem(it.id)} className="bg-destructive text-destructive-foreground" data-testid={`item-delete-confirm-${it.id}`}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <ItemDialog
          open={!!editingItem}
          onOpenChange={(v) => !v && setEditingItem(null)}
          value={editingItem}
          categories={categories}
          onSave={saveItem}
        />
        <CategoryDialog
          open={!!editingCat}
          onOpenChange={(v) => !v && setEditingCat(null)}
          value={editingCat}
          onSave={saveCat}
        />
      </div>
    </AdminLayout>
  );
}
